/**
 * =============================================================================
 * PRNG - Generador de números pseudoaleatorios determinista (seeded)
 * =============================================================================
 *
 * Implementación de mulberry32: rápido, determinista y suficiente para
 * generación procedural. NO usar `Math.random` ni `Date.now` en la generación
 * (rompería el determinismo y está prohibido en scripts del harness).
 *
 * Misma seed ⇒ misma secuencia ⇒ mismo mapa.
 */

export interface Rng {
  /** Float en [0, 1). */
  next(): number;
  /** Entero en [min, max] inclusive. */
  int(min: number, max: number): number;
  /** Bool con probabilidad p (default 0.5). */
  chance(p?: number): boolean;
  /** Elemento aleatorio de un array (undefined si vacío). */
  pick<T>(arr: readonly T[]): T | undefined;
}

/**
 * Crea un RNG determinista a partir de una seed numérica.
 */
export function createRng(seed: number): Rng {
  // Estado interno de 32 bits.
  let state = seed >>> 0;
  // Evitar estado 0 (mulberry32 degenera).
  if (state === 0) {
    state = 0x9e3779b9;
  }

  const next = (): number => {
    state |= 0;
    state = (state + 0x6d2b79f5) | 0;
    let t = Math.imul(state ^ (state >>> 15), 1 | state);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };

  const int = (min: number, max: number): number => {
    if (max < min) {
      return min;
    }
    return min + Math.floor(next() * (max - min + 1));
  };

  const chance = (p = 0.5): boolean => next() < p;

  const pick = <T>(arr: readonly T[]): T | undefined => {
    if (arr.length === 0) {
      return undefined;
    }
    return arr[Math.floor(next() * arr.length)];
  };

  return { next, int, chance, pick };
}

/**
 * Hash de dos coordenadas + seed → entero de 32 bits.
 * Útil para derivar sub-seeds estables por celda/bloque sin avanzar el RNG global.
 */
export function hash2(x: number, y: number, seed: number): number {
  let h = (seed ^ 0x27d4eb2f) >>> 0;
  h = Math.imul(h ^ (x >>> 0), 0x85ebca6b) >>> 0;
  h = Math.imul(h ^ (y >>> 0), 0xc2b2ae35) >>> 0;
  h ^= h >>> 16;
  return h >>> 0;
}
